import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  GridActionsCellItem,
  type GridColDef,
  type GridRowSelectionModel,
} from "@mui/x-data-grid";
// import BoltIcon from "@mui/icons-material/Bolt"; // dùng lại khi bật nút "Kiểm tra"
import AssessmentIcon from "@mui/icons-material/Assessment";
import BlockIcon from "@mui/icons-material/Block";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import { api, downloadExport, localizeServerMessage } from "../lib/api";
import { describeCheckError } from "../lib/check-error";
import { useAsync } from "../lib/hooks";
import { externalUrl, formatMs, newestFirst } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { LinkAuditModal } from "../components/LinkAuditModal";
import { LinkMatrixView } from "../components/LinkMatrix";
import type {
  AdminUser,
  Brand,
  BrandLink,
  BulkDeleteResult,
  BulkOwnerResult,
  HealthStatus,
  ImportResult,
  User,
} from "../lib/types";
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  ErrorCell,
  Field,
  Loading,
  Modal,
} from "../components/ui";
import { DataTable } from "../components/DataTable";

type Filter = "all" | HealthStatus;
/** Đang xem link hoạt động hay danh sách link đã disable (để khôi phục). */
type View = "active" | "disabled";

/**
 * Quyền sửa/xoá 1 link (khớp `canManageOwned` phía API):
 * superadmin/admin đụng được mọi link trong phạm vi của họ; user thường chỉ
 * link mình sở hữu — link chưa có chủ (`ownerId = null`) coi như của chung brand.
 */
function canManage(link: BrandLink, me: User): boolean {
  if (me.role === "superadmin" || me.role === "admin") return true;
  return link.ownerId === null || link.ownerId === me.id;
}

/** Không chọn dòng nào — dùng cho lần đầu và sau mỗi lần đổi chủ hàng loạt. */
const NO_SELECTION: GridRowSelectionModel = { type: "include", ids: new Set() };

/**
 * Danh sách user để chọn chủ sở hữu. `/users` chỉ superadmin gọi được nên chỉ
 * họ mới chọn được chủ khác — user khác nhận mảng rỗng và không thấy ô chọn.
 */
function useOwnerOptions(enabled: boolean) {
  const [owners, setOwners] = useState<AdminUser[]>([]);
  useEffect(() => {
    if (!enabled) return;
    api.users
      .list()
      .then(us => setOwners(us.filter(u => !u.disabled)))
      .catch(() => setOwners([]));
  }, [enabled]);
  return owners;
}

/** Ô chọn chủ sở hữu dùng chung cho form 1 link và đổi chủ hàng loạt. */
function OwnerSelect({
  owners,
  value,
  onChange,
  allowShared,
}: {
  owners: AdminUser[];
  value: string;
  onChange: (v: string) => void;
  /** Cho phép chọn "chung brand" (`ownerId = null`). */
  allowShared: boolean;
}) {
  const { t } = useI18n();
  return (
    <select value={value} onChange={e => onChange(e.target.value)}>
      {allowShared && <option value="">{t("links.ownerShared")}</option>}
      {owners.map(u => (
        <option key={u.id} value={u.id}>
          {u.username}
          {u.brand ? ` · ${u.brand}` : ""}
        </option>
      ))}
    </select>
  );
}

export function BrandLinks({
  navigate,
  me,
}: {
  navigate: (to: string) => void;
  me: User;
}) {
  const { t, fmtDate, fmtAgo } = useI18n();
  const [view, setView] = useState<View>("active");
  const { data, loading, error, reload } = useAsync(
    () => api.brandLinks.list(view === "active"),
    [view],
  );
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [onlyMine, setOnlyMine] = useState(false);
  const [editing, setEditing] = useState<BrandLink | "new" | null>(null);
  const [importing, setImporting] = useState(false);
  const [selection, setSelection] = useState<GridRowSelectionModel>(NO_SELECTION);
  const [bulkOwner, setBulkOwner] = useState(false);
  const [bulkDelete, setBulkDelete] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [displayMode, setDisplayMode] = useState<"list" | "matrix">("list");
  const [auditLink, setAuditLink] = useState<BrandLink | { id: number; brand: string; domain: string; healthStatus?: HealthStatus } | null>(null);

  const links = data ?? [];

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return links.filter(l => {
      if (filter !== "all" && l.healthStatus !== filter) return false;
      if (onlyMine && l.ownerId !== me.id) return false;
      if (!q) return true;
      return (
        l.brand.toLowerCase().includes(q) ||
        l.domain.toLowerCase().includes(q) ||
        (l.ownerUsername ?? "").toLowerCase().includes(q) ||
        (l.note ?? "").toLowerCase().includes(q)
      );
    });
  }, [links, query, filter, onlyMine, me.id]);

  // Lọc/tìm xong mà vẫn ở trang cũ thì bảng trống hoặc thiếu dòng — về trang đầu.
  useEffect(() => {
    setPaginationModel(m => (m.page === 0 ? m : { ...m, page: 0 }));
  }, [query, filter, onlyMine, view]);

  // Đổi tab active/disabled thì bỏ chọn — id đang tick thuộc danh sách cũ,
  // không còn khớp danh sách vừa tải.
  useEffect(() => {
    setSelection(NO_SELECTION);
  }, [view]);

  // Chỉ superadmin đổi được chủ sở hữu (API `/users` cũng chỉ họ gọi được)
  const canPickOwner = me.role === "superadmin";

  /**
   * Id đang chọn, luôn giao với danh sách đang hiển thị: "chọn tất cả" của
   * DataGrid trả về `type: "exclude"` (chọn mọi dòng TRỪ `ids`) nên phải quy về
   * danh sách thật, nếu không sẽ gửi nhầm cả link đang bị bộ lọc ẩn đi.
   */
  const selectedIds = useMemo(() => {
    const ids = visible.map(l => l.id);
    return selection.type === "exclude"
      ? ids.filter(id => !selection.ids.has(id))
      : ids.filter(id => selection.ids.has(id));
  }, [selection, visible]);

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(link: BrandLink) {
    if (!confirm(t("links.confirmDelete", { domain: link.domain, brand: link.brand }))) return;
    await run(`del-${link.id}`, () => api.brandLinks.remove(link.id));
  }

  async function restore(link: BrandLink) {
    if (!confirm(t("links.confirmRestore", { domain: link.domain, brand: link.brand }))) return;
    await run(`restore-${link.id}`, () => api.brandLinks.restore(link.id));
  }

  const columns = useMemo<GridColDef<BrandLink>[]>(
    () => [
      { field: "brand", headerName: t("table.brand"), flex: 0.7, minWidth: 110 },
      {
        field: "domain",
        headerName: t("table.domain"),
        flex: 1.6,
        minWidth: 180,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", minWidth: 0 }}>
            <Link
              href={externalUrl(row.domain)}
              target="_blank"
              rel="noreferrer noopener"
              title={row.domain}
              sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12.5 }}
              noWrap
            >
              {row.domain}
            </Link>
            <Tooltip title={t("links.auditHistory")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAuditLink(row);
                }}
                sx={{ p: 0.25, opacity: 0.6, "&:hover": { opacity: 1 } }}
              >
                <AssessmentIcon sx={{ fontSize: 15 }} color="primary" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
      {
        field: "healthStatus",
        headerName: t("table.status"),
        flex: 0.6,
        minWidth: 110,
        renderCell: ({ row }) => <Badge status={row.healthStatus} />,
      },
      {
        field: "ownerUsername",
        headerName: t("table.owner"),
        flex: 0.7,
        minWidth: 115,
        renderCell: ({ row }) =>
          row.ownerId === null ? (
            <Tooltip title={t("links.ownerSharedHint")}>
              <span className="muted ellipsis">{t("links.ownerShared")}</span>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                row.ownerId === me.id
                  ? `${row.ownerUsername ?? `#${row.ownerId}`} (${t("links.ownerMe")})`
                  : row.ownerUsername ?? `#${row.ownerId}`
              }
            >
              <span className="ellipsis">
                {row.ownerUsername ?? `#${row.ownerId}`}
                {row.ownerId === me.id && (
                  <span className="muted"> · {t("links.ownerMe")}</span>
                )}
              </span>
            </Tooltip>
          ),
      },
      {
        field: "lastStatusCode",
        headerName: t("table.http"),
        flex: 0.4,
        minWidth: 80,
        type: "number",
        valueFormatter: (v: number | null) => v ?? "—",
      },
      {
        field: "lastResponseMs",
        headerName: t("table.response"),
        flex: 0.5,
        minWidth: 95,
        type: "number",
        valueFormatter: (v: number | null) => formatMs(v),
      },
      {
        field: "lastDeviceName",
        headerName: t("table.device"),
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ row }) => (
          <Tooltip title={t("table.deviceHint")}>
            <span>
              {row.lastDeviceName ?? (row.lastDeviceId ? `#${row.lastDeviceId}` : "—")}
            </span>
          </Tooltip>
        ),
      },
      {
        field: "lastCheckerIp",
        headerName: t("table.checkerIp"),
        flex: 0.7,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Tooltip title={t("table.checkerIpHint")}>
            <span className="mono">{row.lastCheckerIp ?? "—"}</span>
          </Tooltip>
        ),
      },
      {
        field: "lastError",
        headerName: t("table.error"),
        flex: 1.2,
        minWidth: 160,
        renderCell: ({ row }) => {
          const err = describeCheckError(
            t,
            { code: row.lastErrorCode, statusCode: row.lastStatusCode, detail: row.lastErrorDetail },
            row.lastError,
          );
          return <ErrorCell error={err} />;
        },
      },
      {
        field: "healthCheckedAt",
        headerName: t("table.checkedAt"),
        flex: 0.8,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Tooltip title={fmtDate(row.healthCheckedAt)}>
            <span>{fmtAgo(row.healthCheckedAt)}</span>
          </Tooltip>
        ),
      },
      {
        field: "expiryAt",
        headerName: t("table.expiry"),
        flex: 0.8,
        minWidth: 120,
        valueGetter: (v: string | null) => (v ? fmtDate(v) : "—"),
      },
      {
        field: "note",
        headerName: t("table.note"),
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Tooltip title={row.note || ""}>
            <span className="muted ellipsis" style={{ maxWidth: "100%" }}>
              {row.note || "—"}
            </span>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 88,
        getActions: ({ row }) =>
          view === "disabled"
            ? [
                <GridActionsCellItem
                  key="audit"
                  icon={
                    <Tooltip title={t("links.auditHistory")}>
                      <AssessmentIcon fontSize="small" color="primary" />
                    </Tooltip>
                  }
                  label={t("links.auditHistory")}
                  onClick={() => setAuditLink(row)}
                />,
                // Link đã disable: chỉ còn tác vụ khôi phục (sửa/xoá tay không áp dụng).
                <GridActionsCellItem
                  key="restore"
                  icon={<RestoreIcon fontSize="small" />}
                  label={canManage(row, me) ? t("links.restore") : t("links.notOwner")}
                  disabled={!canManage(row, me)}
                  onClick={() => restore(row)}
                  showInMenu
                />,
              ]
            : [
                <GridActionsCellItem
                  key="audit"
                  icon={
                    <Tooltip title={t("links.auditHistory")}>
                      <AssessmentIcon fontSize="small" color="primary" />
                    </Tooltip>
                  }
                  label={t("links.auditHistory")}
                  onClick={() => setAuditLink(row)}
                />,
                <GridActionsCellItem
                  key="auditMenu"
                  icon={<AssessmentIcon fontSize="small" />}
                  label={t("links.auditHistory")}
                  onClick={() => setAuditLink(row)}
                  showInMenu
                />,
                <GridActionsCellItem
                  key="compare"
                  icon={<CompareArrowsIcon fontSize="small" />}
                  label={t("links.compare")}
                  onClick={() => navigate(`checks?linkId=${row.id}`)}
                  showInMenu
                />,
                // Link của user khác → khoá sửa/xoá (API cũng trả 403)
                <GridActionsCellItem
                  key="edit"
                  icon={<EditOutlinedIcon fontSize="small" />}
                  label={canManage(row, me) ? t("common.edit") : t("links.notOwner")}
                  disabled={!canManage(row, me)}
                  onClick={() => setEditing(row)}
                  showInMenu
                />,
                <GridActionsCellItem
                  key="disable"
                  icon={<BlockIcon fontSize="small" />}
                  label={canManage(row, me) ? t("links.disable") : t("links.notOwner")}
                  disabled={!canManage(row, me)}
                  onClick={() => remove(row)}
                  showInMenu
                />,
              ],
      },
    ],
    [t, fmtDate, fmtAgo, busy, me, view],
  );

  const counts = {
    all: links.length,
    mine: links.filter(l => l.ownerId === me.id).length,
    healthy: links.filter(l => l.healthStatus === "healthy").length,
    unhealthy: links.filter(l => l.healthStatus === "unhealthy").length,
    unknown: links.filter(l => l.healthStatus === "unknown").length,
  };

  const filterLabel = (f: Filter) => (f === "all" ? t("common.all") : t(`status.${f}`));

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("links.title")}</h1>
          <p className="muted">
            {t(view === "disabled" ? "links.subtitleDisabled" : "links.subtitle", {
              n: links.length,
            })}
          </p>
        </div>
        <div className="row-actions">
          <Button
            loading={busy === "export-csv"}
            onClick={() => run("export-csv", () => downloadExport("csv"))}
          >
            {t("links.exportCsv")}
          </Button>
          <Button
            loading={busy === "export-json"}
            onClick={() => run("export-json", () => downloadExport("json"))}
          >
            {t("links.exportJson")}
          </Button>
          <Button onClick={() => setImporting(true)}>{t("links.import")}</Button>
          {/*
            TẮT kiểm tra trực tiếp từ CMS — xem ghi chú ở cột "actions".
            <Button
              loading={busy === "check-all"}
              onClick={() => run("check-all", () => api.brandLinks.checkAll())}
            >
              {t("dash.checkAll")}
            </Button>
          */}
          <Button variant="primary" onClick={() => setEditing("new")}>
            {t("links.add")}
          </Button>
        </div>
      </header>

      {error && <ErrorBox message={error} />}
      {actionError && <ErrorBox message={actionError} />}

      <Card>
        <div className="toolbar">
          <input
            className="search"
            placeholder={t("links.search")}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div className="segmented">
            {(["all", "healthy", "unhealthy", "unknown"] as Filter[]).map(f => (
              <button
                key={f}
                className={filter === f ? "seg-btn seg-btn-active" : "seg-btn"}
                onClick={() => setFilter(f)}
              >
                {filterLabel(f)} <span className="count">{counts[f]}</span>
              </button>
            ))}
          </div>
          <div className="segmented">
            <button
              className={onlyMine ? "seg-btn seg-btn-active" : "seg-btn"}
              onClick={() => setOnlyMine(v => !v)}
              title={t("links.onlyMineHint")}
            >
              {t("links.onlyMine")} <span className="count">{counts.mine}</span>
            </button>
          </div>
          <div className="segmented">
            <button
              className={view === "active" ? "seg-btn seg-btn-active" : "seg-btn"}
              onClick={() => setView("active")}
            >
              {t("links.viewActive")}
            </button>
            <button
              className={view === "disabled" ? "seg-btn seg-btn-active" : "seg-btn"}
              onClick={() => setView("disabled")}
            >
              {t("links.viewDisabled")}
            </button>
          </div>
          <div className="segmented">
            <button
              className={displayMode === "list" ? "seg-btn seg-btn-active" : "seg-btn"}
              onClick={() => setDisplayMode("list")}
              title={t("links.viewList")}
            >
              📋 {t("links.viewList")}
            </button>
            <button
              className={displayMode === "matrix" ? "seg-btn seg-btn-active" : "seg-btn"}
              onClick={() => setDisplayMode("matrix")}
              title={t("links.viewMatrix")}
            >
              📊 {t("links.viewMatrix")}
            </button>
          </div>
        </div>

        {view === "disabled" && <p className="muted">{t("links.disabledHint")}</p>}

        {view === "active" && selectedIds.length > 0 && displayMode === "list" && (
          <div className="bulk-bar">
            <span>{t("links.bulkSelected", { n: selectedIds.length })}</span>
            <Button variant="danger" onClick={() => setBulkDelete(true)}>
              {t("links.bulkDelete")}
            </Button>
            {canPickOwner && (
              <Button variant="primary" onClick={() => setBulkOwner(true)}>
                {t("links.bulkOwner")}
              </Button>
            )}
            <Button onClick={() => setSelection(NO_SELECTION)}>{t("links.bulkClear")}</Button>
          </div>
        )}

        {loading ? (
          <Loading />
        ) : displayMode === "matrix" ? (
          <Box sx={{ mt: 1 }}>
            <LinkMatrixView onSelectLink={(mLink) => setAuditLink(mLink)} />
          </Box>
        ) : (
          <DataTable
            rows={newestFirst(visible, "createdAt")}
            columns={columns}
            showToolbar
            loading={busy !== null}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            checkboxSelection={view === "active"}
            rowSelectionModel={selection}
            onRowSelectionModelChange={setSelection}
          />
        )}
      </Card>

      {editing && (
        <LinkForm
          link={editing === "new" ? null : editing}
          me={me}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {bulkDelete && (
        <BulkDeleteModal
          ids={selectedIds}
          onClose={() => setBulkDelete(false)}
          onDone={() => {
            setSelection(NO_SELECTION);
            reload();
          }}
        />
      )}

      {bulkOwner && (
        <BulkOwnerModal
          ids={selectedIds}
          onClose={() => setBulkOwner(false)}
          onDone={() => {
            setSelection(NO_SELECTION);
            reload();
          }}
        />
      )}

      {importing && (
        <ImportModal me={me} onClose={() => setImporting(false)} onDone={() => reload()} />
      )}

      {auditLink && (
        <LinkAuditModal link={auditLink} onClose={() => setAuditLink(null)} />
      )}
    </div>
  );
}

function LinkForm({
  link,
  me,
  onClose,
  onSaved,
}: {
  link: BrandLink | null;
  me: User;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [brand, setBrand] = useState(link?.brand ?? "");
  const [domain, setDomain] = useState(link?.domain ?? "");
  const [note, setNote] = useState(link?.note ?? "");
  // <input type="date"> cần định dạng yyyy-mm-dd.
  const [expiry, setExpiry] = useState(link?.expiryAt ? link.expiryAt.slice(0, 10) : "");
  // "" = link chung brand (không có chủ). Link mới mặc định thuộc về mình.
  const [owner, setOwner] = useState(
    link ? (link.ownerId ? String(link.ownerId) : "") : String(me.id),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Chỉ superadmin gọi được /users → chỉ họ chọn được chủ sở hữu khác.
  const canPickOwner = me.role === "superadmin";
  const owners = useOwnerOptions(canPickOwner);
  const canPickBrand = me.role === "superadmin" || (me.role === "admin" && me.country === "br");
  const brands = useBrandOptions(canPickBrand);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      brand,
      domain,
      expiryAt: expiry ? new Date(expiry).toISOString() : null,
      note: note.trim() || null,
    };
    // Không đủ quyền đổi chủ → không gửi field, server tự gán/giữ nguyên
    const ownerId = owner === "" ? null : Number(owner);
    try {
      if (link) {
        await api.brandLinks.update(link.id, {
          ...payload,
          ...(canPickOwner ? { ownerId } : {}),
        });
      } else {
        await api.brandLinks.create({
          ...payload,
          ...(canPickOwner && ownerId !== null ? { ownerId } : {}),
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={link ? t("links.formEdit", { id: link.id }) : t("links.formNew")}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form">
        {error && <ErrorBox message={error} />}
        <Field label={t("table.brand")}>
          <input
            list={canPickBrand ? "brand-suggestions" : undefined}
            value={brand}
            onChange={e => setBrand(e.target.value)}
            required
            minLength={1}
          />
          {canPickBrand && (
            <datalist id="brand-suggestions">
              {brands.map(b => (
                <option key={b.id} value={b.name} />
              ))}
            </datalist>
          )}
        </Field>
        <Field label={t("table.domain")} hint={t("links.domainHint")}>
          <input
            value={domain}
            onChange={e => setDomain(e.target.value)}
            required
            minLength={3}
            placeholder="https://"
          />
        </Field>
        <Field label={t("links.expiryLabel")} hint={t("common.optional")}>
          <input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} />
        </Field>
        <Field label={t("links.note")} hint={t("links.noteHint")}>
          <input
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={t("links.notePlaceholder")}
          />
        </Field>
        {canPickOwner ? (
          <Field label={t("table.owner")} hint={t("links.ownerHint")}>
            {/* Link mới luôn phải có chủ; chỉ khi sửa mới gỡ được về "chung brand" */}
            <OwnerSelect
              owners={owners}
              value={owner}
              onChange={setOwner}
              allowShared={link !== null}
            />
          </Field>
        ) : (
          <Field label={t("table.owner")} hint={t("links.ownerReadonlyHint")}>
            <input value={link?.ownerUsername ?? me.username} disabled />
          </Field>
        )}
        <div className="form-actions">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {link ? t("links.save") : t("links.create")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Đổi chủ sở hữu cho nhiều link cùng lúc. Server xử lý từng link độc lập: link
 * nào không hợp lệ (chủ mới khác brand) chỉ bị bỏ qua và báo trong `errors`,
 * nên vẫn hiện kết quả sau khi chạy thay vì đóng ngay.
 */
/**
 * Xoá (disable) nhiều link đã tick. Quyền do server xét theo từng link: chủ sở
 * hữu xoá link của mình, superadmin/admin xoá mọi link trong brand — link bị từ
 * chối hiện trong danh sách lỗi chứ không làm hỏng cả lô.
 */
function BulkDeleteModal({
  ids,
  onClose,
  onDone,
}: {
  ids: number[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  // Chốt id lúc mở modal — `onDone` bỏ tick nên prop `ids` rỗng đi ngay sau khi
  // chạy xong, trong khi modal vẫn đang hiện kết quả.
  const [targetIds] = useState(ids);
  const [result, setResult] = useState<BulkDeleteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.brandLinks.bulkRemove(targetIds));
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("links.bulkDeleteTitle", { n: targetIds.length })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.close")}</Button>
          {/* Đã xoá xong thì không cho bấm lại */}
          {!result && (
            <Button variant="danger" loading={busy} onClick={submit}>
              {t("links.bulkDeleteApply")}
            </Button>
          )}
        </>
      }
    >
      {error && <ErrorBox message={error} />}

      {!result && (
        <Typography variant="body2">
          {t("links.bulkDeleteConfirm", { n: targetIds.length })}
        </Typography>
      )}

      {result && (
        <div className="import-result">
          <Typography variant="body2">
            {t("links.bulkDeleteDone", {
              disabled: result.disabled,
              requested: result.requested,
            })}
          </Typography>
          {result.errors.length > 0 && (
            <ul className="error-list">
              {result.errors.map(e => (
                <li key={e.id}>
                  {t("links.bulkErrorRow", {
                    id: e.id,
                    error: localizeServerMessage(e.error),
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

function BulkOwnerModal({
  ids,
  onClose,
  onDone,
}: {
  ids: number[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const owners = useOwnerOptions(true);
  // Chốt danh sách id lúc mở modal: `onDone` bỏ tick ở bảng nên prop `ids` rỗng
  // đi ngay sau khi chạy xong, trong khi modal vẫn đang hiện kết quả.
  const [targetIds] = useState(ids);
  // "" = gỡ chủ, link thành của chung brand
  const [owner, setOwner] = useState("");
  const [result, setResult] = useState<BulkOwnerResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.brandLinks.bulkUpdateOwner(targetIds, owner === "" ? null : Number(owner));
      setResult(res);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("links.bulkOwnerTitle", { n: targetIds.length })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.close")}</Button>
          {/* Đã chạy xong thì không cho bấm lại — id đã đổi chủ, chạy lại vô nghĩa */}
          {!result && (
            <Button variant="primary" loading={busy} onClick={submit}>
              {t("links.bulkApply")}
            </Button>
          )}
        </>
      }
    >
      {error && <ErrorBox message={error} />}

      <Field label={t("table.owner")} hint={t("links.bulkOwnerHint")}>
        <OwnerSelect owners={owners} value={owner} onChange={setOwner} allowShared />
      </Field>

      {result && (
        <div className="import-result">
          <Typography variant="body2">
            {t("links.bulkDone", { updated: result.updated, requested: result.requested })}
          </Typography>
          {result.errors.length > 0 && (
            <ul className="error-list">
              {result.errors.map(e => (
                <li key={e.id}>
                  {t("links.bulkErrorRow", {
                    id: e.id,
                    error: localizeServerMessage(e.error),
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Danh mục brand để chọn khi import. `/brands` chỉ superadmin gọi được — vai
 * trò khác luôn bị server ép về brand của chính mình nên không cần ô chọn.
 */
function useBrandOptions(enabled: boolean) {
  const [brands, setBrands] = useState<Brand[]>([]);
  useEffect(() => {
    if (!enabled) return;
    api.brands
      .list()
      .then(setBrands)
      .catch(() => setBrands([]));
  }, [enabled]);
  return brands;
}

function ImportModal({
  me,
  onClose,
  onDone,
}: {
  me: User;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<"file" | "csv">("csv");
  const [file, setFile] = useState<File | null>(null);
  const [csv, setCsv] = useState("");
  const [mode, setMode] = useState<"upsert" | "skip">("upsert");
  // Superadmin và admin Brazil chọn được brand; vai trò khác server tự ép về brand của họ.
  const canPickBrand = me.role === "superadmin" || (me.role === "admin" && me.country === "br");
  const brands = useBrandOptions(canPickBrand);
  const [brand, setBrand] = useState("");
  const [note, setNote] = useState("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      if (tab === "file" && !file) throw new Error(t("import.noFile"));
      if (tab === "csv" && !csv.trim()) throw new Error(t("import.noDomains"));
      const res =
        tab === "file"
          ? await api.brandLinks.importFile(
              file!,
              mode,
              brand || undefined,
              note.trim() || undefined,
            )
          : await api.brandLinks.importJson({
              mode,
              csv,
              brand: brand || undefined,
              note: note.trim() || undefined,
            });
      setResult(res);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("import.title")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.close")}</Button>
          <Button variant="primary" loading={busy} onClick={submit}>
            {t("import.submit")}
          </Button>
        </>
      }
    >
      {canPickBrand && (
        <Field label={t("table.brand")} hint={t("import.brandHint")}>
          <select value={brand} onChange={e => setBrand(e.target.value)}>
            <option value="">{t("import.brandFromData")}</option>
            {brands.map(b => (
              <option key={b.id} value={b.name}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field label={t("import.noteLabel")} hint={t("import.noteHint")}>
        <input
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder={t("import.notePlaceholder")}
        />
      </Field>

      <div className="tabs">
        <button className={tab === "file" ? "tab tab-active" : "tab"} onClick={() => setTab("file")}>
          {t("import.tabFile")}
        </button>
        <button className={tab === "csv" ? "tab tab-active" : "tab"} onClick={() => setTab("csv")}>
          {t("import.tabCsv")}
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      {tab === "file" ? (
        <Field label={t("import.fileLabel")} hint={t("import.fileHint")}>
          <input
            type="file"
            accept=".xlsx,.xls,.csv,.json"
            onChange={e => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
      ) : (
        <Field label={t("import.csvLabel")} hint={t("import.csvHint")}>
          <textarea rows={8} value={csv} onChange={e => setCsv(e.target.value)} className="mono" />
        </Field>
      )}

      <Field label={t("import.modeLabel")}>
        <select value={mode} onChange={e => setMode(e.target.value as "upsert" | "skip")}>
          <option value="upsert">{t("import.modeUpsert")}</option>
          <option value="skip">{t("import.modeSkip")}</option>
        </select>
      </Field>

      {result && (
        <div className="import-result">
          <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
            {(
              [
                ["import.received", result.received, undefined],
                ["import.created", result.created, "success.main"],
                ["import.updated", result.updated, undefined],
                ["import.skipped", result.skipped, "warning.main"],
                ["import.failed", result.failed, "error.main"],
              ] as const
            ).map(([key, value, color]) => (
              <Stack key={key} sx={{ minWidth: 74 }}>
                <Typography variant="h6" sx={{ color, fontWeight: 700, lineHeight: 1.2 }}>
                  {value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(key)}
                </Typography>
              </Stack>
            ))}
          </Stack>
          {result.errors?.length > 0 && (
            <ul className="error-list">
              {result.errors.map(e => (
                <li key={e.row}>
                  {t("import.errorRow", { row: e.row, error: localizeServerMessage(e.error) })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
